/**
 * State-view diff between two consecutive snapshots — Faz 8.1.
 *
 * Diffs are computed per slice (positionStates/freezeStates/...) by
 * key. We classify each key as:
 *   - added: present in `next` but not `prev`
 *   - removed: present in `prev` but not `next`
 *   - changed: present in both, JSON-stringified mismatch
 * Keys where both sides match are hidden so investigators see only
 * what the new event actually mutated.
 */

import type { ReactElement } from "react";

import type { ReplayStateViewWire } from "../api/types.js";

type Slice = keyof ReplayStateViewWire;

const SLICES: readonly Slice[] = [
  "positionStates",
  "reduceOnlyStates",
  "openOrdersStates",
  "freezeStates",
  "realizedPnl",
];

const stringify = (v: unknown): string =>
  JSON.stringify(v, (_, value) => (typeof value === "bigint" ? value.toString() : value));

interface SliceDiff {
  readonly slice: Slice;
  readonly added: ReadonlyArray<readonly [string, unknown]>;
  readonly removed: ReadonlyArray<readonly [string, unknown]>;
  readonly changed: ReadonlyArray<{ key: string; prev: unknown; next: unknown }>;
}

export const computeStateDiff = (
  prev: ReplayStateViewWire | null,
  next: ReplayStateViewWire,
): readonly SliceDiff[] => {
  return SLICES.map((slice) => {
    const prevSlice = prev?.[slice] ?? [];
    const nextSlice = next[slice] ?? [];
    const prevMap = new Map(prevSlice.map(([k, v]) => [k, v] as const));
    const nextMap = new Map(nextSlice.map(([k, v]) => [k, v] as const));

    const added: Array<readonly [string, unknown]> = [];
    const removed: Array<readonly [string, unknown]> = [];
    const changed: Array<{ key: string; prev: unknown; next: unknown }> = [];

    for (const [k, v] of nextMap) {
      if (!prevMap.has(k)) added.push([k, v]);
      else if (stringify(prevMap.get(k)) !== stringify(v))
        changed.push({ key: k, prev: prevMap.get(k), next: v });
    }
    for (const [k, v] of prevMap) {
      if (!nextMap.has(k)) removed.push([k, v]);
    }

    added.sort((a, b) => a[0].localeCompare(b[0]));
    removed.sort((a, b) => a[0].localeCompare(b[0]));
    changed.sort((a, b) => a.key.localeCompare(b.key));

    return { slice, added, removed, changed };
  });
};

export interface StateDiffProps {
  readonly prev: ReplayStateViewWire | null;
  readonly next: ReplayStateViewWire;
}

export const StateDiff = ({ prev, next }: StateDiffProps): ReactElement => {
  const diffs = computeStateDiff(prev, next);
  const hasAny = diffs.some(
    (d) => d.added.length + d.removed.length + d.changed.length > 0,
  );

  return (
    <section className="space-y-3" data-testid="state-diff">
      {!hasAny && (
        <p className="text-xs text-slate-500">No state changes vs. previous tick.</p>
      )}
      {diffs.map((d) => {
        const total = d.added.length + d.removed.length + d.changed.length;
        if (total === 0) return null;
        return (
          <div key={d.slice} className="rounded border border-slate-800">
            <header className="flex items-center justify-between border-b border-slate-800 bg-slate-900/40 px-3 py-1 text-xs uppercase tracking-wide text-slate-300">
              <span>{d.slice}</span>
              <span className="text-slate-500">{total} change(s)</span>
            </header>
            <ul className="divide-y divide-slate-800 text-xs font-mono">
              {d.added.map(([k, v]) => (
                <li key={`add-${k}`} className="bg-emerald-500/10 px-3 py-1">
                  <span className="text-emerald-400">+ {k}</span>{" "}
                  <span className="text-slate-300">{stringify(v)}</span>
                </li>
              ))}
              {d.removed.map(([k, v]) => (
                <li key={`rm-${k}`} className="bg-rose-500/10 px-3 py-1">
                  <span className="text-rose-400">- {k}</span>{" "}
                  <span className="text-slate-400 line-through">{stringify(v)}</span>
                </li>
              ))}
              {d.changed.map((c) => (
                <li key={`ch-${c.key}`} className="bg-amber-500/10 px-3 py-1">
                  <span className="text-amber-400">~ {c.key}</span>
                  <div className="ml-4 text-slate-400 line-through">
                    {stringify(c.prev)}
                  </div>
                  <div className="ml-4 text-slate-200">{stringify(c.next)}</div>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </section>
  );
};
